import {
  type HeadlineResponseOrganizationResponseRelatedPagedData,
  OrganizationType,
  v1OrganizationsHeadlinesDetail,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { getIdFromParam } from '@on3/ui-lib/utils/url';
import { type AxiosError } from 'axios';
import { FullHighSchoolNews } from 'components/HighSchool/FullHighSchoolNews';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';
import { usePathname } from 'next/navigation';
import { createCanonicalUrl } from 'utils/meta';

interface IHighSchoolNewsPageProps extends IGenericPageProps {
  highSchoolArticles?: HeadlineResponseOrganizationResponseRelatedPagedData;
}
const HighSchoolNewsPage = ({
  highSchoolArticles,
  siteData,
  pageType,
  ...props
}: IHighSchoolNewsPageProps) => {
  const { relatedModel } = highSchoolArticles ?? {};
  const title = `${relatedModel?.fullName} News`;
  const description = `The lates news about ${relatedModel?.fullName} from On3`;
  const noIndex = highSchoolArticles?.pagination?.count === 0;
  const pathname = usePathname();
  const canonical = createCanonicalUrl(pathname);

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      noIndex={noIndex}
      pageType={pageType}
      siteData={siteData}
      title={title}
    >
      <FullHighSchoolNews
        highSchoolArticles={highSchoolArticles}
        title={title}
      />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const highSchoolSiteKey = 402;
  const siteData = await withValidUrl.isValid(ctx, highSchoolSiteKey);
  const userData = await withAuthentication.isAuth(ctx);
  const { id, page = 1 } = ctx.query as { id: string; page: string };

  const orgKey = getIdFromParam(id);

  if (!siteData || !orgKey) {
    return {
      notFound: true,
    };
  }

  try {
    const params = {
      page: +page,
      useTag: true,
      orgKey,
    };

    const [highSchoolArticles] = await Promise.allSettled([
      v1OrganizationsHeadlinesDetail(proxyApi, params),
    ]);

    const articleData = getFulfilledValue(highSchoolArticles);

    const { relatedModel } = articleData || {};

    const orgType = relatedModel?.orgTypeEnum;
    const orgSlug = relatedModel?.urlSlug;

    if (orgType !== OrganizationType.HighSchool) {
      return { notFound: true };
    }

    if (id !== orgSlug) {
      return {
        redirect: {
          permanent: true,
          destination: `/high-school/${orgSlug}/news/`,
        },
      };
    }

    const data = {
      pageType: 'article',
      siteData: siteData || [],
      userData: userData || {},
      highSchoolArticles: getFulfilledValue(highSchoolArticles),
    };

    return { props: { ...data } };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.error(err);

    return { props: [] };
  }
};

export default withValidUrl(HighSchoolNewsPage);
